import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { inr, num, dateFmt } from "@/lib/format";
import { PageLoader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Link2 } from "lucide-react";

const STATUSES = ["pending", "processing", "shipped", "delivered", "cancelled"];

export default function ReprintOrders() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-orders"],
    queryFn: async () => (await api.get("/admin/reprint-orders")).data,
  });
  const [tracking, setTracking] = useState({});

  const update = useMutation({
    mutationFn: async ({ id, body }) => (await api.put(`/admin/reprint-orders/${id}`, body)).data,
    onSuccess: () => {
      toast.success("Order updated");
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Reprint orders</p>
        <p className="text-sm text-muted-foreground mt-1">Author orders for printed copies at printing rates (minimum 10 copies). Add a tracking link so authors can follow their parcel.</p>
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <PageLoader />
        ) : data.length === 0 ? (
          <EmptyState icon={Printer} title="No reprint orders" desc="Author print orders will appear here." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead>Author</TableHead>
                  <TableHead>Book</TableHead>
                  <TableHead>Delivery</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tracking link</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((o) => (
                  <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                    <TableCell className="font-medium">{o.author_name}<div className="text-xs text-muted-foreground font-normal font-mono">{o.order_no || ""} · {dateFmt(o.created_at)}</div></TableCell>
                    <TableCell>{o.book_title}</TableCell>
                    <TableCell className="text-muted-foreground max-w-[220px]">
                      <span className="block truncate" title={o.shipping_address}>{o.shipping_address}</span>
                      <span className="block text-xs">{[o.mobile, o.pincode].filter(Boolean).join(" · ") || "—"}</span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{num(o.quantity)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{inr(o.total_amount)}</TableCell>
                    <TableCell>
                      <span data-testid={`admin-payment-${o.id}`} className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${o.payment_status === "paid" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400" : (o.payment_status === "pending" ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400" : "bg-muted text-muted-foreground")}`}>
                        {o.payment_status === "paid" ? "Paid" : (o.payment_status === "pending" ? "Pending" : (o.payment_status === "unpaid" || !o.payment_status ? "Unpaid" : "Failed"))}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Select value={o.status} onValueChange={(v) => update.mutate({ id: o.id, body: { status: v } })}>
                        <SelectTrigger className="h-8 w-[130px]" data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1.5 items-center min-w-[220px]">
                        <Input
                          data-testid={`order-tracking-${o.id}`}
                          className="h-8"
                          placeholder="https://track..."
                          defaultValue={o.tracking_link || ""}
                          onChange={(e) => setTracking({ ...tracking, [o.id]: e.target.value })}
                        />
                        <Button size="icon" variant="outline" className="h-8 w-8 shrink-0" data-testid={`save-tracking-${o.id}`} onClick={() => update.mutate({ id: o.id, body: { tracking_link: tracking[o.id] ?? o.tracking_link ?? "" } })}>
                          <Link2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
